import { Plus, Trash2, AlertCircle, Building2, FileText, Info, MapPin, UserRoundCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Button } from "@/components/atoms/Button";
import { CopyButton } from "@/components/atoms/CopyButton";
import { Input } from "@/components/atoms/Input";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { AddressPreview } from "@/components/molecules/AddressPreview";
import { propertiesService } from "@/services/propertiesService";
import { formatPrice } from "@/lib/format";
import { useCountries } from "@/hooks/useCountries";
import { normalizeCountryCode } from "@/modules/contacts/contactFormUtils";
import {
  isLeaseholdTenure,
  PROPERTY_MARKET_OPTIONS,
  type PropertyFormValues,
  type PropertyListingReadOnly,
} from "./propertyFormUtils";

interface SelectOption {
  value: string;
  label: string;
}

interface Props {
  register: UseFormRegister<PropertyFormValues>;
  watch: UseFormWatch<PropertyFormValues>;
  setValue: UseFormSetValue<PropertyFormValues>;
  branchOptions: SelectOption[];
  loadingBranches?: boolean;
  onBranchOpen?: () => void;
  onBranchEndReached?: () => void;
  negotiatorOptions: SelectOption[];
  loadingNegotiators?: boolean;
  onNegotiatorOpen?: () => void;
  onNegotiatorEndReached?: () => void;
  ownerContactIds: string[];
  ownerOptions: SelectOption[];
  loadingContacts?: boolean;
  onOwnerChange: (index: number, contactId: string) => void;
  onAddOwner: () => void;
  onRemoveOwner: (index: number) => void;
  onOwnerOpen?: () => void;
  onOwnerEndReached?: () => void;
  onContactSearch?: (query: string) => void;
  onCreateOwnerClick?: () => void;
  isCreate?: boolean;
  submitAttempted?: boolean;
  listingDetails?: PropertyListingReadOnly | null;
}

export function PropertyFormFields({
  register,
  watch,
  setValue,
  branchOptions,
  loadingBranches,
  onBranchOpen,
  onBranchEndReached,
  negotiatorOptions,
  loadingNegotiators,
  onNegotiatorOpen,
  onNegotiatorEndReached,
  ownerContactIds,
  ownerOptions,
  loadingContacts,
  onOwnerChange,
  onAddOwner,
  onRemoveOwner,
  onOwnerOpen,
  onOwnerEndReached,
  onContactSearch,
  isCreate = false,
  submitAttempted = false,
  listingDetails,
}: Props) {
  const market = watch("market");
  const branchId = watch("branchId");
  const negotiatorId = watch("negotiatorId");
  const addressNameNumber = watch("addressNameNumber");
  const postcode = watch("postcode");
  const countryCode = watch("countryCode");
  const leaseYearsRemaining = watch("leaseYearsRemaining");
  const leaseEndDate = watch("leaseEndDate");
  const tenureType = watch("tenureType");
  const showLease = isLeaseholdTenure(tenureType);
  const showValidation = isCreate && submitAttempted;
  const hasOwner = ownerContactIds.some((id) => id.trim());
  const normalizedCountryCode = normalizeCountryCode(countryCode ?? "");
  const leaseYears = leaseYearsRemaining?.trim() ?? "";
  const leaseEnd = leaseEndDate?.trim() ?? "";
  const leaseYearsInvalid =
    Boolean(leaseYears) &&
    (Number.isNaN(Number.parseInt(leaseYears, 10)) || Number.parseInt(leaseYears, 10) < 0);
  const leaseEndInPast = (() => {
    if (!leaseEnd) return false;
    const endDate = new Date(leaseEnd);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate < today;
  })();
  const [tenureOptions, setTenureOptions] = useState<SelectOption[]>([]);
  const [loadingTenureOptions, setLoadingTenureOptions] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingTenureOptions(true);
    void propertiesService
      .getLeaseTenureTypes()
      .then((items) => {
        if (cancelled) return;
        setTenureOptions(items.map(({ value, label }) => ({ value, label })));
      })
      .catch(() => {
        if (cancelled) return;
        setTenureOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTenureOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tenureSelectOptions = useMemo(() => {
    const current = tenureType?.trim();
    if (!current || tenureOptions.some((option) => option.value === current)) {
      return tenureOptions;
    }
    return [{ value: current, label: current }, ...tenureOptions];
  }, [tenureOptions, tenureType]);

  const locked = !isCreate;
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const { countryCodeOptions, loading: loadingCountries } = useCountries();

  const listingRows = useMemo(() => {
    if (!listingDetails) return [];
    const rows: { label: string; value: string }[] = [];
    if (listingDetails.bedrooms != null) {
      rows.push({ label: "Beds", value: String(listingDetails.bedrooms) });
    }
    if (listingDetails.bathrooms != null) {
      rows.push({ label: "Bathrooms", value: String(listingDetails.bathrooms) });
    }
    if (listingDetails.price != null) {
      rows.push({
        label: "Price",
        value: formatPrice(listingDetails.price, listingDetails.currency),
      });
    }
    if (listingDetails.rentalFrequency) {
      rows.push({ label: "Rental frequency", value: listingDetails.rentalFrequency });
    }
    if (listingDetails.currency) {
      rows.push({ label: "Currency", value: listingDetails.currency });
    }
    return rows;
  }, [listingDetails]);

  const propertyAddress = {
    addressSubDwelling: watch("addressSubDwelling"),
    addressNameNumber: watch("addressNameNumber"),
    street: watch("street"),
    locality: watch("locality"),
    town: watch("town"),
    county: watch("county"),
    postcode: watch("postcode"),
  };
  const addressCopyValue = [
    [propertyAddress.addressSubDwelling, propertyAddress.addressNameNumber, propertyAddress.street],
    [propertyAddress.locality, propertyAddress.town, propertyAddress.county],
    [propertyAddress.postcode],
  ]
    .map((parts) => parts.map((part) => part?.trim()).filter(Boolean).join(", "))
    .filter(Boolean)
    .join(", ");

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      {isCreate ? (
        <p className="text-xs text-bridge-muted">
          <span className="text-red-500">*</span> Required field
        </p>
      ) : null}

      <PropertyFormSection title="Basic Information" icon={Info}>
        <PropertyFormRow>
          <Field label="Market" required={isCreate}>
            <MenuSelect
              hideLabel
              value={market ?? ""}
              onChange={(v) => setValue("market", v ?? "", { shouldDirty: true, shouldValidate: true })}
              options={[...PROPERTY_MARKET_OPTIONS]}
              placeholder="Select market"
            />
            <FieldValidationError
              show={showValidation && !market?.trim()}
              message="Market is required"
            />
          </Field>
          <Field label="Branch" required={isCreate}>
            <MenuSelect
              hideLabel
              disabled={locked}
              value={branchId ?? ""}
              onChange={(v) => setValue("branchId", v ?? "", { shouldDirty: true, shouldValidate: true })}
              options={branchOptions}
              placeholder={loadingBranches ? "Loading branches…" : "Select branch"}
              loading={loadingBranches}
              onOpen={onBranchOpen}
              onDropdownEndReached={onBranchEndReached}
            />
            <FieldValidationError
              show={showValidation && !branchId?.trim()}
              message="Branch is required"
            />
          </Field>
        </PropertyFormRow>
        <PropertyFormRow>
          <Field label="Negotiator" required={isCreate}>
            <MenuSelect
              hideLabel
              disabled={locked}
              value={negotiatorId ?? ""}
              onChange={(v) =>
                setValue("negotiatorId", v ?? "", { shouldDirty: true, shouldValidate: true })
              }
              options={negotiatorOptions}
              placeholder={
                loadingNegotiators ? "Loading negotiators…" : "Select negotiator"
              }
              loading={loadingNegotiators}
              onOpen={onNegotiatorOpen}
              onDropdownEndReached={onNegotiatorEndReached}
            />
            <FieldValidationError
              show={showValidation && !negotiatorId?.trim()}
              message="Negotiator is required"
            />
          </Field>
        </PropertyFormRow>
      </PropertyFormSection>

      {listingRows.length > 0 ? (
        <PropertyFormSection title="Listing Details" icon={Building2}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {listingRows.map((row) => (
              <div
                key={row.label}
                className="rounded-xl border border-bridge-border/60 bg-white px-4 py-3 shadow-sm"
              >
                <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-bridge-muted">
                  {row.label}
                </div>
                <div className="mt-1 text-sm font-medium text-bridge-fg">
                  {row.value}
                </div>
              </div>
            ))}
          </div>
        </PropertyFormSection>
      ) : null}

      <PropertyFormSection title="Address" icon={MapPin}>
        {isCreate ? (
          <>
            <PropertyFormRow>
              <Field label="Sub Dwelling">
                <Input
                  className="w-full"
                  placeholder="Flat, unit, etc."
                  {...register("addressSubDwelling")}
                />
              </Field>
              <Field label="Name/Number" required>
                <Input
                  className="w-full"
                  placeholder="e.g. 12, 12A, or Rose Cottage"
                  {...register("addressNameNumber", { required: "Name/Number is required" })}
                />
                <FieldValidationError
                  show={showValidation && !addressNameNumber?.trim()}
                  message="Name/Number is required"
                />
              </Field>
            </PropertyFormRow>
            <PropertyFormRow>
              <Field label="Street">
                <Input
                  className="w-full"
                  placeholder="e.g. High Street"
                  {...register("street")}
                />
              </Field>
              <Field label="Town">
                <Input
                  className="w-full"
                  placeholder="e.g. London"
                  {...register("town")}
                />
              </Field>
            </PropertyFormRow>
            <PropertyFormRow>
              <Field label="Locality">
                <Input className="w-full" {...register("locality")} />
              </Field>
              <Field label="County">
                <Input className="w-full" {...register("county")} />
              </Field>
            </PropertyFormRow>
            <PropertyFormRow>
              <Field label="Postcode" required>
                <Input
                  className="w-full"
                  placeholder="e.g. EC3N 4AC"
                  {...register("postcode", { required: "Postcode is required" })}
                />
                <FieldValidationError
                  show={showValidation && !postcode?.trim()}
                  message="Postcode is required"
                />
              </Field>
              <Field label="UPRN">
                <Input
                  className="w-full"
                  placeholder="Unique Property Reference Number"
                  {...register("uprn")}
                />
              </Field>
            </PropertyFormRow>
            <PropertyFormRow>
              <Field label="Country Code">
                <MenuSelect
                  hideLabel
                  value={countryCode ?? ""}
                  onChange={(v) => setValue("countryCode", v, { shouldDirty: true })}
                  options={countryCodeOptions}
                  placeholder="Select country"
                  loading={loadingCountries}
                  searchable
                />
                <FieldValidationError
                  show={
                    showValidation &&
                    Boolean(countryCode?.trim()) &&
                    normalizedCountryCode.length !== 2
                  }
                  message="Country code must be 2 letters (e.g. GB)"
                />
              </Field>
            </PropertyFormRow>
          </>
        ) : (
          <AddressPreview
            addressSubDwelling={propertyAddress.addressSubDwelling}
            addressNameNumber={propertyAddress.addressNameNumber}
            street={propertyAddress.street}
            locality={propertyAddress.locality}
            town={propertyAddress.town}
            county={propertyAddress.county}
            postcode={propertyAddress.postcode}
            action={<CopyButton value={addressCopyValue} label="Address" />}
          />
        )}
      </PropertyFormSection>

      <PropertyFormSection title="Ownership" icon={UserRoundCheck}>
        <div className="space-y-4">
          {ownerContactIds.map((ownerId, index) => (
            <div key={`owner-${index}`} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <Field
                  label={index === 0 ? "Owner" : `Owner ${index + 1}`}
                  required={isCreate && index === 0}
                >
                  <MenuSelect
                    hideLabel
                    disabled={locked}
                    value={ownerId}
                    onChange={(v) => onOwnerChange(index, v)}
                    options={ownerOptions}
                    placeholder={
                      ownerOptions.length
                        ? "Search by name or address"
                        : loadingContacts
                          ? "Loading contacts…"
                          : "Search contacts…"
                    }
                    searchable
                    searchInTrigger
                    searchPlaceholder="Search owners…"
                    searchDebounceMs={300}
                    loading={loadingContacts}
                    onOpen={onOwnerOpen}
                    onDropdownSearchQueryChange={onContactSearch}
                    onDropdownEndReached={onOwnerEndReached}
                  />
                  {index === 0 ? (
                    <FieldValidationError
                      show={showValidation && !hasOwner}
                      message="Owner is required"
                    />
                  ) : null}
                </Field>
              </div>
              {!locked && ownerContactIds.length > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="shrink-0 text-bridge-muted hover:text-bridge-danger"
                  onClick={() => onRemoveOwner(index)}
                  aria-label={`Remove owner ${index + 1}`}
                >
                  <Trash2 size={16} />
                </Button>
              ) : null}
            </div>
          ))}
          {!locked ? (
            <div className="flex flex-wrap gap-2 pl-[7.5rem]">
              <Button type="button" variant="ghost" size="sm" onClick={onAddOwner}>
                <Plus size={14} className="mr-1" />
                Add owner
              </Button>
            </div>
          ) : null}
        </div>
      </PropertyFormSection>

      <PropertyFormSection title="Lease Details" icon={FileText}>
        <PropertyFormRow>
          <Field label="Tenure">
            <MenuSelect
              hideLabel
              disabled={locked}
              value={tenureType ?? ""}
              onChange={(v) => {
                setValue("tenureType", v ?? "", { shouldDirty: true, shouldValidate: true });
                if (!isLeaseholdTenure(v)) {
                  setValue("leaseYearsRemaining", "", { shouldDirty: true });
                  setValue("leaseEndDate", "", { shouldDirty: true });
                }
              }}
              options={tenureSelectOptions}
              placeholder={loadingTenureOptions ? "Loading tenure…" : "Select tenure"}
              loading={loadingTenureOptions}
            />
          </Field>
        </PropertyFormRow>
        {showLease ? (
          <>
            <PropertyFormRow>
              <Field label="Remaining Term (Years)">
                <Input
                  type="number"
                  min={0}
                  step={1}
                  className="w-full"
                  placeholder="e.g. 99"
                  disabled={locked}
                  {...register("leaseYearsRemaining")}
                />
                <FieldValidationError
                  show={showValidation && leaseYearsInvalid}
                  message="Enter a valid remaining term in years"
                />
              </Field>
              <Field label="Lease End Date">
                <Input
                  type="date"
                  min={todayStr}
                  className="w-full [&>input]:min-w-[10.5rem]"
                  placeholder="YYYY-MM-DD"
                  {...register("leaseEndDate")}
                />
                <FieldValidationError
                  show={showValidation && leaseEndInPast}
                  message="Lease End Date must not be in the past"
                />
              </Field>
            </PropertyFormRow>
            <FieldValidationError
              show={showValidation && !leaseYears && !leaseEnd}
              message="Provide Remaining Term (Years) or Lease End Date for leasehold tenure"
              className="pl-[7.5rem]"
            />
            <p className="text-xs text-bridge-muted">
              Provide at least one of Remaining Term or Lease End Date for leasehold-related tenure.
            </p>
          </>
        ) : (
          <p className="text-sm text-bridge-muted">
            Select a leasehold-related tenure to enter lease term details.
          </p>
        )}
      </PropertyFormSection>
    </div>
  );
}

function PropertyFormSection({
  title,
  icon: Icon,
  children,
}: {
  title?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-5 rounded-2xl border border-bridge-border/50 bg-bridge-surface/30 p-4 shadow-sm sm:p-5">
      {title ? (
        <div className="flex items-center gap-3">
          {Icon ? (
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#009BF5]/10 text-[#009BF5]">
              <Icon className="h-4 w-4" />
            </span>
          ) : (
            <span className="h-5 w-1.5 rounded-full bg-[#009BF5]" aria-hidden />
          )}
          <h3 className="text-sm font-semibold text-bridge-fg">{title}</h3>
        </div>
      ) : null}
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function PropertyFormRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 items-center gap-4 lg:grid-cols-2 lg:gap-x-10">
      {children}
    </div>
  );
}

function Field({
  label,
  children,
  required = false,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <label className="w-[7.25rem] shrink-0 pt-2.5 text-sm text-bridge-muted">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function FieldValidationError({
  show,
  message,
  className,
}: {
  show: boolean;
  message: string;
  className?: string;
}) {
  if (!show) return null;
  return (
    <div
      className={`mt-1 flex animate-in items-center gap-1.5 text-xs font-medium text-red-500 fade-in slide-in-from-top-1 duration-200${className ? ` ${className}` : ""}`}
    >
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}
